import { connect, Dispatch } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import { IApplicationState, AppActionCreators, FileActionCreators, CenterActionCreators } from '@nkr/store/index';
import { FileDocumentType, OrganType } from '@nkr/enums/index';
import { downloadFile } from '@nkr/services/download-file';
import { useAppHookContext } from '@nkr/utils/rest-api';
import { ICardDef } from './card-def';
import ComplianceCard from './compliance-card';
import React from 'react';
import Expectations from './expectations';

interface IProps {
    centerId: number;
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>;
    hideLoading: () => void;
    downloadCenterFile: (docType: number, centerId: number) => Promise<any>;
    getCmsFile: (centerId: number) => Promise<any>;
    getPatientOutcomes: (centerId: number) => Promise<any>;
}

type TProps = IProps & IDispatchProps;

const RegulatoryCompliance = (props: TProps) => {
    const { centerId } = props;
    const { app } = useAppHookContext();
    const isLiver = app.site.organType == OrganType.Liver;

    const handleDownload = (docType: FileDocumentType, filename: string) => {
        props.showLoading('Loading document...').then(() => {
            props.downloadCenterFile(docType, props.centerId)
                .then((response: any) => {
                    downloadFile(response.data, filename, response.headers?.contentType || 'application/pdf');
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    }

    const handleDownloadCms = () => {
        props.showLoading('Loading document...').then(() => {
            props.getCmsFile(props.centerId)
                .then((response: any) => {
                    downloadFile(response.data, `center${props.centerId}_cms.pdf`, response.headers.contentType);
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    }

    const handleDownloadPatientOutcomes = () => {
        props.showLoading('Loading document...').then(() => {
            props.getPatientOutcomes(props.centerId)
                .then((response: any) => {
                    downloadFile(response.data, `center${props.centerId}_patient_outcomes.pdf`, response.headers.contentType || 'application/pdf');
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    }

    const liverCards: ICardDef[] = [
        {
            id: 'serology-blood-draws',
            icon: '/images/tile-icon-OPTN-Blood-Draw.svg',
            title: 'Serology Donor Blood Draws',
            subtitle: '(Center Specific)',
            bgColor: 'rgba(74, 6, 88, 1)',
            hoverColor: 'rgba(74, 6, 88, 1)',
            hoverText: 'Review donor blood draw and donation date information to help verify compliance with testing requirements.',
            action: () => { window.location.href = `/center/${centerId}/reports/my-center/serology-blood-draws`; }
        }
    ];

    const kidneyCards: ICardDef[] = [
        {
            id: 'nkr-agreement',
            icon: '/images/tile-icon-NKR-Aggreement.svg',
            title: 'NKR Agreement',
            subtitle: '(Contract Documentation)',
            bgColor: 'rgba(107, 10, 56, 1)',
            hoverColor: 'rgba(107, 10, 56, 1)',
            hoverText: "Access your center's current participation agreement with the NKR. This will assist in demonstrating formal contractual arrangements supporting transplant program operations.",
            action: () => { window.location.href = `/center/${centerId}/legal`; }
        },
        {
            id: 'cms-cert',
            icon: '/images/tile-icon-CMS-Certification.svg',
            title: 'CMS Certification',
            subtitle: '("Letter of Good Standing")',
            bgColor: 'rgba(17, 52, 89, 1)',
            hoverColor: 'rgba(17, 52, 89, 1)',
            hoverText: "Download your center's most recent CMS certification letter on file with the NKR, confirming Medicare approval of the transplant program.",
            action: () => handleDownloadCms()
        },
        {
            id: 'swap-quality',
            icon: '/images/tile-icon-Swap-Quality-Issues-Report.svg',
            title: 'Swap Quality Issues Report',
            subtitle: '(Center Specific)',
            bgColor: 'rgba(59, 118, 181, 1)',
            hoverColor: 'rgba(59, 118, 181, 1)',
            hoverText: 'View quality and operational data related to kidney paired exchange transplants involving your center. These reports may assist transplant programs with internal quality monitoring, performance review, and QAPI discussions.',
            action: () => { window.location.href = `/center/${centerId}/action-center/swap-quality-issues/list/all?reportedBy=3`; }
        },
        {
            id: 'patient-outcomes',
            icon: '/images/tile-icon-Patient-Outcomes.svg',
            title: 'Patient Outcomes',
            subtitle: '(SRTR Patient & Graft Survival Data)',
            bgColor: 'rgba(0, 94, 93, 1)',
            hoverColor: 'rgba(0, 94, 93, 1)',
            hoverText: 'Download patient and graft survival outcomes for your center as reported through the Scientific Registry of Transplant Recipients (SRTR). These reports may be useful for transplant program performance monitoring and quality review.',
            action: () => handleDownloadPatientOutcomes()
        },
        {
            id: 'paired-exchange-outcomes',
            icon: '/images/tile-icon-Paired-Exchange-Outcomes-Reporting.svg',
            title: 'Paired Exchange Outcomes Reporting',
            subtitle: '',
            bgColor: 'rgba(0, 133, 115, 1)',
            hoverColor: 'rgba(0, 133, 115, 1)',
            hoverText: 'Access NKR outcomes reports summarizing results from the paired exchange program. These reports may support transplant program evaluation and quality discussions.',
            action: () => window.open('https://www.kidneyregistry.com/news-category/outcomes-reports/', '_blank')
        },
        {
            id: 'paired-exchange-performance',
            icon: '/images/tile-icon-Paired-Exchange-Performance-Reporting.svg',
            title: 'Paired Exchange Performance Reporting',
            subtitle: '',
            bgColor: 'rgba(135, 24, 157, 1)',
            hoverColor: 'rgba(135, 24, 157, 1)',
            hoverText: 'Review operational and clinical performance metrics associated with the NKR paired exchange program. These materials may assist centers in understanding program performance and trends.',
            action: () => window.open('https://www.kidneyregistry.com/news-category/quarterly-reports/', '_blank')
        },
        {
            id: 'donor-satisfaction',
            icon: '/images/tile-icon-Living-Donor-Satisfaction-Survey-Results.svg',
            title: 'Living Donor Satisfaction Survey Results',
            subtitle: '(Center Specific)',
            bgColor: 'rgba(227, 82, 5, 1)',
            hoverColor: 'rgba(227, 82, 5, 1)',
            hoverText: 'View aggregated satisfaction feedback from living donors associated with your center. Donor experience information may support transplant program quality review and ongoing assessment of donor care and support services.',
            action: () => { window.location.href = `/center/${centerId}/reports/dash-board/center-five-star-surveys`; }
        },
        {
            id: 'serology-final-xm-blood-draws',
            icon: '/images/tile-icon-OPTN-Blood-Draw.svg',
            title: 'Serology and Final XM Donor Blood Draws',
            subtitle: '(Center Specific)',
            bgColor: 'rgba(74, 6, 88, 1)',
            hoverColor: 'rgba(74, 6, 88, 1)',
            hoverText: 'Review donor blood draw, final crossmatch and donation date information to help verify compliance with testing requirements.',
            action: () => { window.location.href = `/center/${centerId}/reports/my-center/serology-final-xm-blood-draws`; }
        },
        {
            id: 'network-email-repository',
            icon: '/images/tile-icon-NKR-Aggreement.svg',
            title: 'NKR Network Email Repository',
            subtitle: '(Center Specific)',
            bgColor: 'rgba(46, 125, 50, 1)',
            hoverColor: 'rgba(46, 125, 50, 1)',
            hoverText: 'Review NKR network communications sent to your center. This record may support survey readiness by documenting notices and operational updates received from the NKR.',
            action: () => { window.location.href = `/center/${centerId}/network-email-repository`; }
        }
    ];

    const cards: ICardDef[] = isLiver ? liverCards : kidneyCards;

    return (
        <div style={{ padding: '0px 15px 20px' }}>
            <h4 style={{ fontWeight: 700, marginBottom: 8, textTransform: 'uppercase' }}>
                Compliance &amp; Survey Readiness Resource Hub
            </h4>
            <p style={{ color: '#555', marginBottom: 25 }}>
                The resources below provide centralized access to documents and data that may assist your center in meeting regulatory expectations and maintaining survey and audit readiness.
            </p>
            {isLiver ?
                <Row>
                    {cards.map(card => (
                        <Col key={card.id} xs={12} sm={6} md={3} style={{ marginBottom: 20 }}>
                            <ComplianceCard card={card} />
                        </Col>
                    ))}
                </Row>
                :
                <>
                    <Row>
                        {cards.slice(0, 4).map(card => (
                            <Col key={card.id} xs={12} sm={6} md={3} style={{ marginBottom: 20 }}>
                                <ComplianceCard card={card} />
                            </Col>
                        ))}
                    </Row>
                    <Row>
                        {cards.slice(4).map(card => (
                            <Col key={card.id} xs={12} sm={6} md={3} style={{ marginBottom: 20 }}>
                                <ComplianceCard card={card} />
                            </Col>
                        ))}
                    </Row>
                </>
            }

            <Expectations isLiver={isLiver} />
        </div>
    );
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => ({
    showLoading: (message) => {
        return dispatch(AppActionCreators.showLoadingAsync(message))
    },
    hideLoading: () => {
        dispatch(AppActionCreators.hideLoading())
    },
    downloadCenterFile: (docType, centerId) => {
        return dispatch(FileActionCreators.downloadFile(docType, centerId))
    },
    getCmsFile: (centerId) => {
        return dispatch(CenterActionCreators.getCmsFile(centerId))
    },
    getPatientOutcomes: (centerId) => {
        return dispatch(CenterActionCreators.getPatientOutcomes(centerId))
    }
});

// export default connect<{}, IDispatchProps, IProps>(null, mapDispatchToProps)(RegulatoryCompliance);

export default connect<{}, IDispatchProps, IProps>(
    null,
    mapDispatchToProps)(RegulatoryCompliance);
